<template>
    <b-modal :id="id" size="xl" hide-footer>
        <template v-slot:modal-title>
            {{ $t('postalCode.modal.title') }}
            <font-awesome-icon icon="search"></font-awesome-icon>
        </template>
        <b-row>
            <b-col>
                <vSelect
                    :dynamicWidth="false"
                    :options="getCountries"
                    :select-on-tab="true"
                    :placeholder="$t('postalCode.modal.selectCountry')"
                    name="postalCodesModal_Country"
                    label="countryName"
                    @update="$v.selectedCountryCode.$touch"
                    v-model="selectedCountryCode"
                    :clearable="false"
                    :reduce="(country) => country.countryCode"
                />
                <b-form-invalid-feedback>
                    <validationField
                        :fieldName="'Country'"
                        v-if="$v.selectedCountryCode.$error"
                        :validator="$v.selectedCountryCode"
                    ></validationField>
                </b-form-invalid-feedback>
            </b-col>
            <b-col>
                <b-form-input
                    size="sm"
                    v-model="city"
                    name="postalCodesModal_City"
                    :class="{ 'is-invalid': $v.city.$error }"
                    :placeholder="$t('postalCode.modal.city')"
                    @update="$v.city.$touch()"
                    @keydown.enter.native="getPostalCodes()"
                ></b-form-input>
                <b-form-invalid-feedback>
                    <validationField
                        :fieldName="'City'"
                        v-if="$v.city.$error"
                        :validator="$v.city"
                    ></validationField>
                </b-form-invalid-feedback>
            </b-col>
            <b-col>
                <b-form-input
                    size="sm"
                    name="postalCodesModal_PostalCode"
                    v-model="postalCode"
                    :placeholder="$t('postalCode.modal.postalCode')"
                    :class="{ 'is-invalid': $v.postalCode.$error }"
                    @update="$v.postalCode.$touch()"
                    @keydown.enter.native="getPostalCodes()"
                ></b-form-input>
                <b-form-invalid-feedback v-if="$v.postalCode.$error">
                    <validationField
                        :fieldName="'Postal code'"
                        v-if="$v.postalCode.$invalid"
                        :validator="$v.postalCode"
                    ></validationField>
                </b-form-invalid-feedback>
            </b-col>
            <b-col class="text-center">
                <b-button @click="getPostalCodes()" size="sm" name="postalCodesModal_Filter"
                    >Filter</b-button
                >
            </b-col>
        </b-row>
        <b-row class="mt-4">
            <b-col>
                <b-pagination
                    v-if="postalCodes.length > 0"
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    align="fill"
                ></b-pagination>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-table
                    :busy="isBusy"
                    fixed
                    select-mode="single"
                    ref="postal-code-table"
                    show-empty
                    :items="postalCodes"
                    :fields="fields"
                    no-provider-paging
                >
                    <template v-slot:table-busy>
                        <div class="text-center my-2">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>Loading...</strong>
                        </div>
                    </template>
                    <template v-slot:cell(to)="data">
                        {{ data.item.to }}
                        <b-button
                            name="postalCodesModal_selectButton"
                            v-if="selectable"
                            style="float: right"
                            class="mr-3 pa-0 mt-0 mb-0"
                            size="sm"
                            @click="setPostalCode(data.item)"
                            >{{ $t('postalCode.modal.selectPostalCode') }}</b-button
                        >
                    </template>
                    <template v-slot:empty>
                        <p style="text-align: center">
                            {{ $t('postalCode.modal.emptyPostalCodes') }}
                        </p>
                    </template>
                </b-table>
            </b-col>
        </b-row>
    </b-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import axios from 'axios';
import vSelect from './vSelect';
import { ifError } from 'assert';
import { ACTIONS, MUTATIONS, CREATE_SHIPMENT_PREFIX } from '../store/constants';
import { requiredIf, required, maxLength } from 'vuelidate/lib/validators';
import validationField from './Validation/ValidationField';

export default {
    components: {
        vSelect,
        validationField
    },
    data() {
        return {
            currentPage: 1,
            perPage: 100,
            id: '',
            city: '',
            fields: [
                {
                    key: 'city',
                    label: 'City',
                    sortable: true
                },
                {
                    key: 'from',
                    label: 'Postal code from',
                    sortable: true
                },
                {
                    key: 'to',
                    label: 'Postal code to',
                    sortable: true
                }
            ],
            postalCode: '',
            postalCodes: [],
            selectedCountryCode: '',
            selectedPostalCode: [],
            totalRows: 0,
            isBusy: false
        };
    },
    validations: {
        postalCode: {
            required: requiredIf(function() {
                return !this.city || 0 === this.city.length;
            }),
            maxLength: maxLength(10)
        },
        city: {
            required: requiredIf(function() {
                return !this.postalCode || 0 === this.postalCode.length;
            })
        },
        selectedCountryCode: {
            required
        }
    },
    props: {
        selectable: {
            type: Boolean,
            default: false
        },
        refPrefix: {
            type: String
        },
        countryCode: {
            type: String,
            default: null
        }
    },
    computed: {
        ...mapGetters('core', ['getCountries'])
    },
    created() {
        this.id = this.refPrefix ? this.refPrefix + '_postal-code-modal' : '_postal-code-modal';
        this.selectedCountryCode = this.countryCode;
    },
    watch: {
        countryCode: function() {
            this.selectedCountryCode = this.countryCode;
        },
        currentPage: {
            immediate: true,
            handler(val, oldVal) {
                if (!oldVal) {
                    return;
                }
                this.currentPage = val;
                this.getPostalCodes();
            }
        }
    },
    methods: {
        setPostalCode(postalCode) {
            if (postalCode.from !== postalCode.to) {
                //Postal is in a range
                this.$emit('inputPostalCode', {
                    postalCode: this.postalCode,
                    city: postalCode.city,
                    countryCode: this.selectedCountryCode
                });
            } else {
                this.$emit('inputPostalCode', {
                    postalCode: postalCode.from,
                    city: postalCode.city,
                    countryCode: this.selectedCountryCode
                });
            }
            
            this.$bvModal.hide(this.id);
        },
        isRowSelected(index) {
            return this.$refs['postal-code-table'].isRowSelected(index);
        },

        async getPostalCodes() {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }
            if (this.selectedCountryCode) {
                const params = {
                    countryIso: this.selectedCountryCode,
                    pageSize: this.perPage,
                    pageNumber: this.currentPage
                };

                if (this.postalCode.length > 0) {
                    params.postalCode = this.postalCode;
                }
                if (this.city.length > 0) {
                    params.city = this.city;
                }

                this.isBusy = true;

                const data = await this.$store.dispatch(
                    CREATE_SHIPMENT_PREFIX + ACTIONS.CREATESHIPMENT.FETCH_POSTAL_CODES,
                    params
                );

                this.isBusy = false;

                if (this.totalRows !== data.numberOfRows) {
                    this.currentPage = 1;
                }
                this.postalCodes = data.postalCodes;
                this.totalRows = data.numberOfRows;

            }
        }
    }
};
</script>
